import {
  compileKMachine,
  extractKResultComponent,
  initialVertex,
  normalizeEdge,
} from '../engine/ku'
import type {
  CompiledKMachine,
  KComplex,
  KExecutionState,
  KLayout,
  KMachine,
  KRule,
  KSignature,
  KTypeId,
  KVertex,
} from '../engine/types'

export type TreeDirection = 'L' | 'R'
export type TreeValue = 'a' | 'b' | 'c' | 'd'

export type BinaryTree =
  | { value: TreeValue }
  | { left: BinaryTree; right: BinaryTree }

const VALUE_TYPES = ['VAL_A', 'VAL_B', 'VAL_C', 'VAL_D'] as const
const TREE_NODE_TYPES = [
  'ROOT',
  'L0',
  'R0',
  'L1',
  'R1',
  'L2',
  'R2',
] as const
const ADDRESS_TYPES = ['P0', 'P1', 'P2', 'BIT_L', 'BIT_R', 'END'] as const

export const TREE_LOOKUP_SIGNATURE: KSignature = {
  types: ['T0', 'T1', ...TREE_NODE_TYPES, ...ADDRESS_TYPES, ...VALUE_TYPES],
  workingInitialType: 'T0',
  finalInitialType: 'T1',
}

function edge(a: string, b: string) {
  return normalizeEdge({ a, b })
}

function valueType(value: TreeValue): KTypeId {
  return `VAL_${value.toUpperCase()}`
}

function phaseAfter(phase: number): number {
  return (phase + 1) % 3
}

function phaseBefore(phase: number): number {
  return (phase + 2) % 3
}

function treeNodeType(side: TreeDirection, depth: number): KTypeId {
  return `${side}${depth % 3}`
}

interface LocalTreeShape {
  currentType: KTypeId
  parentType: KTypeId | null
  leftType: KTypeId
  rightType: KTypeId
}

function internalShapes(): LocalTreeShape[] {
  const shapes: LocalTreeShape[] = [
    { currentType: 'ROOT', parentType: null, leftType: 'L1', rightType: 'R1' },
  ]

  for (let phase = 0; phase < 3; phase++) {
    const childPhase = phaseAfter(phase)
    const parentPhase = phaseBefore(phase)
    for (const side of ['L', 'R'] as const) {
      const currentType = `${side}${phase}`
      const parentTypes: KTypeId[] = [`L${parentPhase}`, `R${parentPhase}`]
      if (phase === 1) parentTypes.unshift('ROOT')
      for (const parentType of parentTypes) {
        shapes.push({
          currentType,
          parentType,
          leftType: `L${childPhase}`,
          rightType: `R${childPhase}`,
        })
      }
    }
  }
  return shapes
}

interface LeafShape {
  currentType: KTypeId
  parentType: KTypeId
}

function leafShapes(): LeafShape[] {
  const shapes: LeafShape[] = []
  for (let phase = 0; phase < 3; phase++) {
    const parentPhase = phaseBefore(phase)
    for (const side of ['L', 'R'] as const) {
      const parentTypes: KTypeId[] = [`L${parentPhase}`, `R${parentPhase}`]
      if (phase === 1) parentTypes.unshift('ROOT')
      for (const parentType of parentTypes) {
        shapes.push({ currentType: `${side}${phase}`, parentType })
      }
    }
  }
  return shapes
}

function routeRule(
  shape: LocalTreeShape,
  addressPhase: number,
  direction: TreeDirection,
  nextKind: 'spine' | 'end',
): KRule {
  const bitType = direction === 'L' ? 'BIT_L' : 'BIT_R'
  const nextType = nextKind === 'spine' ? `P${phaseAfter(addressPhase)}` : 'END'
  const vertices: KVertex[] = [
    { id: 'cursor', type: 'T0' },
    { id: 'current', type: shape.currentType },
    { id: 'address', type: `P${addressPhase}` },
    { id: 'left', type: shape.leftType },
    { id: 'right', type: shape.rightType },
    { id: 'bit', type: bitType },
    { id: 'next', type: nextType },
  ]
  if (shape.parentType) vertices.push({ id: 'parent', type: shape.parentType })

  const inputEdges = [
    edge('cursor', 'current'),
    edge('cursor', 'address'),
    edge('current', 'left'),
    edge('current', 'right'),
    edge('address', 'bit'),
    edge('address', 'next'),
  ]
  if (shape.parentType) inputEdges.push(edge('current', 'parent'))

  const outputVertices: KVertex[] = [
    { id: 'new-cursor', type: 'T0' },
    { id: 'replacement', type: shape.currentType },
    { id: 'left', type: shape.leftType },
    { id: 'right', type: shape.rightType },
    { id: 'bit', type: bitType },
    { id: 'next', type: nextType },
  ]
  if (shape.parentType) outputVertices.push({ id: 'parent', type: shape.parentType })

  const outputEdges = [
    edge('replacement', 'left'),
    edge('replacement', 'right'),
    edge('new-cursor', direction === 'L' ? 'left' : 'right'),
    edge('new-cursor', 'next'),
  ]
  if (shape.parentType) outputEdges.push(edge('replacement', 'parent'))

  const frontier = [
    { inputBoundary: 'left', outputVertex: 'left' },
    { inputBoundary: 'right', outputVertex: 'right' },
    { inputBoundary: 'bit', outputVertex: 'bit' },
    { inputBoundary: 'next', outputVertex: 'next' },
  ]
  if (shape.parentType) {
    frontier.push({ inputBoundary: 'parent', outputVertex: 'parent' })
  }

  const parentLabel = shape.parentType ?? 'none'
  return {
    id: `route-${shape.currentType}-${parentLabel}-P${addressPhase}-${direction}-${nextKind}`,
    title: `Follow ${direction === 'L' ? 'left' : 'right'} child`,
    description:
      `Replace the radius-2 neighborhood, preserve its frontier, and consume one ${direction} address cell.`,
    input: { vertices, edges: inputEdges },
    output: { vertices: outputVertices, edges: outputEdges },
    attachments: frontier,
    tags: ['traversal', direction === 'L' ? 'left' : 'right', nextKind],
  }
}

function finishRule(shape: LeafShape, value: TreeValue): KRule {
  const type = valueType(value)
  return {
    id: `finish-${shape.currentType}-${shape.parentType}-${type}`,
    title: `Return leaf ${value}`,
    description:
      'Replace the working initial vertex with a final initial vertex; the final component is the result.',
    input: {
      vertices: [
        { id: 'cursor', type: 'T0' },
        { id: 'leaf', type: shape.currentType },
        { id: 'end', type: 'END' },
        { id: 'parent', type: shape.parentType },
        { id: 'value', type },
      ],
      edges: [
        edge('cursor', 'leaf'),
        edge('cursor', 'end'),
        edge('leaf', 'parent'),
        edge('leaf', 'value'),
      ],
    },
    output: {
      vertices: [
        { id: 'final', type: 'T1' },
        { id: 'replacement', type: shape.currentType },
        { id: 'parent', type: shape.parentType },
        { id: 'value', type },
      ],
      edges: [
        edge('final', 'replacement'),
        edge('replacement', 'parent'),
        edge('replacement', 'value'),
      ],
    },
    attachments: [
      { inputBoundary: 'parent', outputVertex: 'parent' },
      { inputBoundary: 'value', outputVertex: 'value' },
    ],
    tags: ['finalization', `value-${value}`],
  }
}

export function createTreeLookupMachine(): KMachine {
  const rules: KRule[] = []
  for (const shape of internalShapes()) {
    for (let addressPhase = 0; addressPhase < 3; addressPhase++) {
      for (const direction of ['L', 'R'] as const) {
        rules.push(routeRule(shape, addressPhase, direction, 'spine'))
        rules.push(routeRule(shape, addressPhase, direction, 'end'))
      }
    }
  }
  for (const shape of leafShapes()) {
    for (const value of ['a', 'b', 'c', 'd'] as const) {
      rules.push(finishRule(shape, value))
    }
  }

  return {
    model: 'ku-1958',
    schemaVersion: 1,
    id: 'typed-tree-lookup',
    title: 'Typed tree lookup',
    description:
      'A radius-2 normal-form K-machine that follows a finite L/R address through a typed full binary tree.',
    radius: 2,
    signature: TREE_LOOKUP_SIGNATURE,
    rules,
    provenance: {
      profile: 'kolmogorov-uspensky-1958',
      source: 'Kolmogorov and Uspensky, On the Definition of an Algorithm (1958)',
      implementation: 'reimplementation',
    },
  }
}

export const treeLookupKU = createTreeLookupMachine()
export const compiledTreeLookupKU: CompiledKMachine = compileKMachine(treeLookupKU)

export const WORKED_TREE: BinaryTree = {
  left: { left: { value: 'a' }, right: { value: 'b' } },
  right: { left: { value: 'c' }, right: { value: 'd' } },
}

interface TreeBuildResult {
  rootId: string
  vertices: KVertex[]
  edges: ReturnType<typeof edge>[]
}

function buildTree(
  tree: BinaryTree,
  path: string,
  depth: number,
  side: TreeDirection | null,
): TreeBuildResult {
  const rootId = path.length === 0 ? 'tree-root' : `tree-${path}`
  const type = side ? treeNodeType(side, depth) : 'ROOT'
  const vertices: KVertex[] = [{ id: rootId, type }]
  const edges: ReturnType<typeof edge>[] = []

  if ('value' in tree) {
    const id = `value-${path || 'root'}`
    vertices.push({ id, type: valueType(tree.value) })
    edges.push(edge(rootId, id))
    return { rootId, vertices, edges }
  }

  const left = buildTree(tree.left, `${path}L`, depth + 1, 'L')
  const right = buildTree(tree.right, `${path}R`, depth + 1, 'R')
  vertices.push(...left.vertices, ...right.vertices)
  edges.push(edge(rootId, left.rootId), edge(rootId, right.rootId), ...left.edges, ...right.edges)
  return { rootId, vertices, edges }
}

export function treeLookupInput(
  address: readonly TreeDirection[],
  tree: BinaryTree = WORKED_TREE,
): KComplex {
  const builtTree = buildTree(tree, '', 0, null)
  const vertices = [...builtTree.vertices, { id: 'cursor', type: 'T0' }]
  const edges = [...builtTree.edges]

  for (let index = 0; index < address.length; index++) {
    const direction = address[index]
    vertices.push(
      { id: `address-${index}`, type: `P${index % 3}` },
      { id: `bit-${index}`, type: direction === 'L' ? 'BIT_L' : 'BIT_R' },
    )
    edges.push(edge(`address-${index}`, `bit-${index}`))
    if (index > 0) edges.push(edge(`address-${index - 1}`, `address-${index}`))
  }

  vertices.push({ id: 'address-end', type: 'END' })
  if (address.length > 0) {
    edges.push(
      edge('cursor', 'address-0'),
      edge(`address-${address.length - 1}`, 'address-end'),
    )
  } else {
    edges.push(edge('cursor', 'address-end'))
  }
  edges.push(edge('cursor', builtTree.rootId))

  return { vertices, edges }
}

function isTreeNode(type: KTypeId): boolean {
  return TREE_NODE_TYPES.includes(type as (typeof TREE_NODE_TYPES)[number])
}

function isValue(type: KTypeId): boolean {
  return type.startsWith('VAL_')
}

function neighborIds(complex: KComplex, id: string): string[] {
  const result: string[] = []
  for (const item of complex.edges) {
    if (item.a === id) result.push(item.b)
    else if (item.b === id) result.push(item.a)
  }
  return result
}

export function decodeTreeResult(state: KExecutionState): TreeValue | null {
  const result = extractKResultComponent(state, TREE_LOOKUP_SIGNATURE)
  if (!result) return null
  const byId = new Map(result.vertices.map((vertex) => [vertex.id, vertex]))
  const final = initialVertex(result, TREE_LOOKUP_SIGNATURE)
  const leafId = neighborIds(result, final.id).find((id) => isTreeNode(byId.get(id)?.type ?? ''))
  if (!leafId) return null
  const value = neighborIds(result, leafId)
    .map((id) => byId.get(id)?.type)
    .find((type): type is KTypeId => Boolean(type && isValue(type)))
  if (!value) return null
  return value.slice(4).toLowerCase() as TreeValue
}

function pathIndex(path: string): number {
  let index = 0
  for (const direction of path) index = index * 2 + (direction === 'R' ? 1 : 0)
  return index
}

export function layoutTreeLookup(state: KExecutionState): KLayout {
  const { complex } = state
  const byId = new Map(complex.vertices.map((vertex) => [vertex.id, vertex]))
  const positions: Record<string, { x: number; y: number }> = {}
  const root = complex.vertices.find((vertex) => vertex.type === 'ROOT')
  const treePaths = new Map<string, string>()

  if (root) {
    const visit = (id: string, path: string, parent: string | null) => {
      treePaths.set(id, path)
      const phase = (path.length + 1) % 3
      for (const neighbor of neighborIds(complex, id)) {
        if (neighbor === parent) continue
        const type = byId.get(neighbor)?.type
        if (type === `L${phase}`) visit(neighbor, `${path}L`, id)
        if (type === `R${phase}`) visit(neighbor, `${path}R`, id)
      }
    }
    visit(root.id, '', null)
  }

  for (const [id, path] of treePaths) {
    const divisions = 2 ** path.length
    positions[id] = {
      x: 45 + ((pathIndex(path) + 0.5) / divisions) * 470,
      y: 64 + path.length * 94,
    }
    const valueNeighbor = neighborIds(complex, id).find((neighbor) =>
      isValue(byId.get(neighbor)?.type ?? ''),
    )
    if (valueNeighbor) {
      positions[valueNeighbor] = { x: positions[id].x, y: positions[id].y + 48 }
    }
  }

  const initial = initialVertex(complex, TREE_LOOKUP_SIGNATURE)
  const currentTree = neighborIds(complex, initial.id).find((id) =>
    isTreeNode(byId.get(id)?.type ?? ''),
  )
  const currentPosition = currentTree ? positions[currentTree] : undefined
  const currentPath = currentTree ? treePaths.get(currentTree) : undefined
  positions[initial.id] = currentPosition
    ? currentPath
      ? {
          // Diagonal offset, mirrored by child side, keeps the cursor out of
          // the parent–child corridor: straight above it would sit inside the
          // parent's frontier halo and touch the node below.
          x: currentPosition.x + (currentPath.endsWith('R') ? 64 : -64),
          y: currentPosition.y - 52,
        }
      : { x: currentPosition.x, y: currentPosition.y - 43 }
    : { x: 280, y: 20 }

  const addressStart = neighborIds(complex, initial.id).find((id) => {
    const type = byId.get(id)?.type ?? ''
    return type.startsWith('P') || type === 'END'
  })
  let previous = initial.id
  let current = addressStart
  let addressIndex = 0
  while (current) {
    const type = byId.get(current)?.type ?? ''
    positions[current] = { x: 570 + addressIndex * 82, y: 88 }
    const bit = neighborIds(complex, current).find((id) =>
      (byId.get(id)?.type ?? '').startsWith('BIT_'),
    )
    if (bit) positions[bit] = { x: positions[current]!.x, y: 137 }
    if (type === 'END') break
    const next = neighborIds(complex, current).find((id) => {
      if (id === previous || id === bit) return false
      const nextType = byId.get(id)?.type ?? ''
      return nextType.startsWith('P') || nextType === 'END'
    })
    previous = current
    current = next
    addressIndex++
  }

  const detachedBits = complex.vertices.filter(
    (vertex) => vertex.type.startsWith('BIT_') && !positions[vertex.id],
  )
  detachedBits.forEach((vertex, index) => {
    positions[vertex.id] = { x: 590 + index * 62, y: 260 }
  })

  const unplaced = complex.vertices.filter((vertex) => !positions[vertex.id])
  unplaced.forEach((vertex, index) => {
    positions[vertex.id] = { x: 560 + (index % 4) * 70, y: 315 + Math.floor(index / 4) * 55 }
  })

  return positions
}
